import * as Arcade from '@/features/arcade/types'
import { useEffect, useRef, useState } from 'react'
import { logSessionEvent } from '@/util/logger'
import { calculateRemainingSeconds } from '@/features/arcade/utils'

type Timestamp = string | null

type SeasonTransitionedInput = {
  enabled: boolean
  type: 'seasonStarted' | 'seasonEnded'
  timestamp?: Timestamp
}

type SeasonTransitionedResult = {
  transitioned: boolean
}

const MAX_TIMEOUT = 2147483647 as const

const useSeasonTransitioned = ({
  enabled,
  type,
  timestamp
}: SeasonTransitionedInput): SeasonTransitionedResult => {
  const [transitioned, setTransitioned] = useState(false)

  useEffect(() => {
    if (!enabled) return
    if (!timestamp) return

    const remainingSeconds = calculateRemainingSeconds(timestamp)
    if (remainingSeconds <= 0) return

    const timeoutMs = remainingSeconds * 1000
    if (timeoutMs > MAX_TIMEOUT) return

    logSessionEvent(`[arcade] ${type} will occur in ${timeoutMs} ms`)

    const timeoutId = setTimeout(() => {
      logSessionEvent(`[arcade] ${type} at ${timestamp}`)
      setTransitioned(true)
    }, timeoutMs)

    return () => clearTimeout(timeoutId)
  }, [timestamp])

  return { transitioned }
}

type SeasonStartedInput = {
  season: Pick<Arcade.Season, 'startAt' | 'status'>
  onSeasonStarted: () => void
}

export const useArcadeSeasonStarted = (input: SeasonStartedInput) => {
  const hasFiredRef = useRef(false)
  const { season, onSeasonStarted } = input
  const timestamp = season?.startAt
  const status = season?.status
  const enabled = status === 'next'

  const { transitioned } = useSeasonTransitioned({
    enabled,
    timestamp,
    type: 'seasonStarted'
  })

  const seasonHasStarted = enabled && transitioned && !hasFiredRef.current

  useEffect(() => {
    if (seasonHasStarted) {
      onSeasonStarted()
      hasFiredRef.current = true
    }
  }, [seasonHasStarted, onSeasonStarted])
}

type SeasonEndedInput = {
  season: Pick<Arcade.Season, 'endAt' | 'status'>
  onSeasonEnded: () => void
}

export const useArcadeSeasonEnded = (input: SeasonEndedInput) => {
  const hasFiredRef = useRef(false)
  const { season, onSeasonEnded } = input
  const timestamp = season?.endAt
  const status = season?.status
  const enabled = status === 'current'

  const { transitioned } = useSeasonTransitioned({
    enabled,
    timestamp,
    type: 'seasonEnded'
  })

  const seasonHasEnded = enabled && transitioned && !hasFiredRef.current

  useEffect(() => {
    if (seasonHasEnded) {
      onSeasonEnded()
      hasFiredRef.current = true
    }
  }, [seasonHasEnded, onSeasonEnded])
}
